import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadCsv } from '../api.ts';
import { errorMessage } from '../messages.ts';
import type { Announce } from './useAnnounce.tsx';

/**
 * Scaricare un export, e dirlo.
 *
 * Quattro punti facevano gli stessi sei passaggi: chiedere il file, tradurne le
 * intestazioni, salvarlo, annunciarlo, e — nei due che se ne ricordavano —
 * annunciare il fallimento. Il rischio di quattro copie non è la lunghezza: è
 * che una di esse annunci "file pronto" per un salvataggio che non è avvenuto,
 * che è il difetto corretto in AJ17 e che qui non può più esistere in una copia
 * sola.
 *
 * Le intestazioni del file sono **chiavi di traduzione**, non testo: il server
 * non scrive stringhe per una persona (AJ5), e la traduzione avviene qui, nella
 * lingua di chi ha premuto il pulsante.
 */

/**
 * Le chiavi del file si traducono **così come sono**.
 *
 * Fino a ieri qui c'era `t(key.replace('.', ':'))`, che trasforma
 * `export.minutes` in `export:minutes` — cioè chiede a i18next il namespace
 * `export`, che non esiste. i18next non trova niente, restituisce `minutes`, e
 * il file usciva con le intestazioni in codice macchina: esattamente ciò che
 * AJ5 aveva corretto, e che nessuno vedeva perché il test dei vincoli verifica
 * che la chiave **esista**, non che la si trovi.
 *
 * Le chiavi del progetto sono piatte, con il punto dentro il nome, e il
 * namespace predefinito della console è quello giusto: `t('export.minutes')`
 * risponde "Minuti" (`docs/DECISIONI.md`, AM12).
 */
function translateCell(t: TFunction): (key: string) => string {
  return (key) => t(key);
}

/**
 * Salva un file nel browser.
 *
 * Un `blob:` e un click su un elemento non attaccato al documento: è l'unico
 * modo di far scaricare qualcosa che è arrivato da una `fetch` autenticata.
 */
export function saveFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  /**
   * L'URL si revoca **dopo**, non subito.
   *
   * `revokeObjectURL` sincrono dopo `click()` annulla il salvataggio su Firefox
   * e Safari, che leggono il blob un attimo più tardi. Un `blob:` vivo è un
   * pezzo di dati dell'ente che resta in memoria alla scheda, quindi non resta
   * per sempre (AJ17).
   */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Restituisce la funzione che scarica. `say` è quella di `useAnnounce`: l'esito
 * si annuncia sempre, e "file pronto" si dice **solo** se il file è stato
 * davvero scritto.
 */
export function useCsvExport(say: Announce['say']): (path: string) => Promise<void> {
  const { t } = useTranslation();

  return useCallback(
    async (path: string) => {
      try {
        const file = await downloadCsv(path, translateCell(t));
        saveFile(file.filename, file.content);
        say(t('admin.export_ready', { name: file.filename }));
      } catch (error) {
        say(`${t('admin.export_failed')} ${errorMessage(t, error)}`, 'danger');
      }
    },
    [say, t],
  );
}
